import { AppDispatch, RootState } from "app/store";
import { useAppDispatch, useAppSelector } from "app/hooks";
import { useContext, useState } from "react";
import { AsyncThunkAction } from "@reduxjs/toolkit";
import { EvaluationResponse } from "@genesys/cloud-frontend/dist/mjs/models/EvaluationResponse";
import {
    GuxButton,
    GuxFormFieldTextarea,
    GuxIcon,
    GuxTooltip,
} from "genesys-spark-components-react";
import CommentType from "enums/CommentType";
import {
    HasEditScoreOrEditPermissionsContext,
    RescoreModeContext,
} from "features/qualityForm/common/Contexts";
import { useStrings } from "features/i18n/useStrings";
import strings from "features/qualityForm/formViewer/footer/footer.strings";
import { DefaultLimits } from "constants/DefaultLimits";
import {
    getPreviewScores,
    saveEvaluatorComment,
    savePrivateComment,
    saveReviewComment,
    scorableSliceActions,
    selectAgentHasRead,
    selectEvaluationCommentsHaveContent,
    selectHasAssignPermission,
    selectHasEditScoreOrEditPermissions,
    selectHaveDisputeDeclinePermission,
    selectHaveDisputePermission,
    selectIsCalibration,
    selectIsConversationAgeLimitReached,
    selectIsOperationInProgress,
    selectIsScorableComplete,
    selectIsScorableRetracted,
    selectPrivateComments,
    selectPrivateCommentsHaveContent,
    selectReviewComments,
    selectReviewCommentsHaveContent,
    selectScorable,
} from "features/scorable/scorableSlice";
import { selectFormType } from "features/qualityForm/qualityFormSlices/qualityFormCommons";
import toastService from "services/toastService";
import "./Comments.less";
import OverallComments, {
    OverallAgentComments,
} from "features/qualityForm/formViewer/footer/OverallComments";
import { PreviewModeContext } from "features/qualityForm/common/Contexts";
import { RemainingCharacters } from "features/qualityForm/common/RemainingCharacters";
import FormType from "enums/FormType";
import { toGuxDisabled } from "util/componentUtil";

export enum CommentEditor {
    OPEN = "open",
    STANDALONE = "standalone",
}
interface CommentProps {
    withEditor?: CommentEditor;
    isPreviewMode?: boolean;
}

function CommentsSectionLabel() {
    const t = useStrings(strings);
    return (
        <div
            className="ScorableCommentsSectionLabel"
            data-testid="comments-section-label"
        >
            {t.labels.comments}
        </div>
    );
}

function handleGetPreviewScores(dispatch: AppDispatch, formType?: FormType) {
    dispatch(
        getPreviewScores({
            isScorableFinished: true,
        })
    ).then((e) => {
        if ("error" in e) {
            toastService.notifyMinorAlert(
                `composer.${formType}s.editor.submitValidations.preview.failure.title`,
                {
                    bodyMessageKey: `composer.${formType}s.editor.submitValidations.preview.failure.message`,
                }
            );
        } else {
            toastService.notifyMessage(
                `composer.${formType}s.editor.submitValidations.preview.success.title`,
                {
                    bodyMessageKey: `composer.${formType}s.editor.submitValidations.preview.success.message`,
                    icon: "fa-check-circle",
                },
                false
            );
        }
    });
}

export function GeneralComments({
    context,
    rescoreMode,
}: {
    context: "admin" | "agent";
    rescoreMode: boolean;
}) {
    const { previewMode } = useContext(PreviewModeContext);
    const isNotCalibration = !useAppSelector(selectIsCalibration);
    const hasEditScoreOrEditPermissions =
        useAppSelector(selectHasEditScoreOrEditPermissions) || previewMode;
    const isStatusFinished = useAppSelector(selectIsScorableComplete);
    const isEvaluationComplete = isStatusFinished && !rescoreMode;

    const reviewCommentsExist = useAppSelector(selectReviewCommentsHaveContent);
    const evaluatorCommentsExist = useAppSelector(
        selectEvaluationCommentsHaveContent
    );
    const isAdmin = context === "admin";
    const hasAgentReviewed = useAppSelector(selectAgentHasRead) ?? false;
    const isStatusRetracted = useAppSelector(selectIsScorableRetracted);
    const showCommentsSectionLabel =
        (!isAdmin &&
            (reviewCommentsExist ||
                (!hasAgentReviewed && !isStatusRetracted) ||
                evaluatorCommentsExist)) ||
        (isAdmin &&
            (reviewCommentsExist ||
                evaluatorCommentsExist ||
                !isEvaluationComplete));

    if (!showCommentsSectionLabel) {
        return <></>;
    }

    return (
        <div className="GeneralComments">
            {!isAdmin ? <CommentsSectionLabel /> : <></>}
            <OverallComments
                disableEditing={isEvaluationComplete}
                disableField={!hasEditScoreOrEditPermissions}
            />
            {isNotCalibration && isEvaluationComplete ? (
                <OverallAgentComments context={context} />
            ) : (
                <></>
            )}
        </div>
    );
}
export function AdminComments({
    isPreviewMode = false,
}: {
    isPreviewMode?: boolean;
}) {
    const { rescoreMode } = useContext(RescoreModeContext);
    const isEvaluationComplete =
        useAppSelector(selectIsScorableComplete) && !rescoreMode;
    const isNotCalibration = !useAppSelector(selectIsCalibration);
    const isStatusRetracted = useAppSelector(selectIsScorableRetracted);
    const hasScoringExpired = useAppSelector((state) => {
        return selectIsConversationAgeLimitReached(
            state,
            DefaultLimits.scoringExpiry
        );
    });
    const hasEditScoreOrEditPermissions =
        useContext(HasEditScoreOrEditPermissionsContext)
            .hasEditScoreOrEditPermissions || isPreviewMode;
    const hasDisputePermission = useAppSelector(selectHaveDisputePermission);
    const hasDisputeDeclinePermission = useAppSelector(
        selectHaveDisputeDeclinePermission
    );
    const editReviewCommentsStandalone =
        !hasEditScoreOrEditPermissions &&
        (hasDisputePermission || hasDisputeDeclinePermission);
    const evaluationCommentsEditor =
        !hasScoringExpired &&
        !isStatusRetracted &&
        hasEditScoreOrEditPermissions
            ? isEvaluationComplete
                ? CommentEditor.STANDALONE
                : CommentEditor.OPEN
            : undefined;
    const reviewCommentsEditor =
        !hasScoringExpired &&
        !isStatusRetracted &&
        isEvaluationComplete &&
        editReviewCommentsStandalone
            ? CommentEditor.STANDALONE
            : undefined;
    const privateCommentsEditor = isEvaluationComplete
        ? !hasScoringExpired && !isStatusRetracted
            ? CommentEditor.STANDALONE
            : undefined
        : CommentEditor.OPEN;
    const hasAssignPermission = useAppSelector(selectHasAssignPermission);
    const reviewCommentsExist = useAppSelector(selectReviewCommentsHaveContent);
    const evaluatorCommentsExist = useAppSelector(
        selectEvaluationCommentsHaveContent
    );
    const privateCommentsExist = useAppSelector(
        selectPrivateCommentsHaveContent
    );

    const showAdminComments =
        ((reviewCommentsExist || reviewCommentsEditor !== undefined) &&
            isNotCalibration) ||
        evaluatorCommentsExist ||
        evaluationCommentsEditor !== undefined ||
        ((privateCommentsEditor !== undefined || privateCommentsExist) &&
            isNotCalibration &&
            hasAssignPermission);
    const EvaluatorCommentProps = {
        withEditor: evaluationCommentsEditor,
        isPreviewMode: isPreviewMode,
    };
    const ReviewCommentProps = {
        withEditor: reviewCommentsEditor,
        isPreviewMode: isPreviewMode,
    };
    const PrivateCommentProps = {
        withEditor: privateCommentsEditor,
        isPreviewMode: isPreviewMode,
    };

    if (!showAdminComments) {
        return <></>;
    }

    return (
        <div className="AdminComments">
            <CommentsSectionLabel />
            <EvaluatorComment {...EvaluatorCommentProps} />
            {isNotCalibration && (
                <>
                    <ReviewComment {...ReviewCommentProps} />
                    <PrivateComment {...PrivateCommentProps} />
                </>
            )}
        </div>
    );
}

export function ReviewComment({
    withEditor,
    isPreviewMode = false,
}: CommentProps) {
    const t = useStrings(strings);
    const formType = useAppSelector(selectFormType);
    const comment = useAppSelector((state) => {
        return selectReviewComments(state) ?? "";
    });
    const showReadOnlyComments = comment.trim().length > 0;
    const dispatch = useAppDispatch();
    const onPreviewSaveComment = isPreviewMode
        ? () => handleGetPreviewScores(dispatch, formType)
        : undefined;

    let commentDisplay = <></>;

    switch (withEditor) {
        case CommentEditor.OPEN:
            commentDisplay = (
                <OpenComment
                    label={t.labels.reviewComments}
                    comment={comment}
                    onUpdateComment={scorableSliceActions.fillReviewComments}
                    characterLimit={DefaultLimits.generalCommentCharLimit}
                />
            );
            break;
        case CommentEditor.STANDALONE:
            commentDisplay = (
                <StandaloneComment
                    label={t.labels.reviewComments}
                    comment={comment}
                    canEditIndividually={true}
                    onUpdateComment={scorableSliceActions.fillReviewComments}
                    onSaveComment={saveReviewComment}
                    onPreviewSaveComment={onPreviewSaveComment}
                    characterLimit={DefaultLimits.generalCommentCharLimit}
                />
            );
            break;
        default:
            // read-only mode
            commentDisplay = showReadOnlyComments ? (
                <StandaloneComment
                    label={t.labels.reviewComments}
                    comment={comment}
                    canEditIndividually={false}
                    onUpdateComment={scorableSliceActions.fillReviewComments}
                    onSaveComment={saveReviewComment}
                    onPreviewSaveComment={onPreviewSaveComment}
                />
            ) : (
                <></>
            );
            break;
    }

    return commentDisplay;
}

export function EvaluatorComment({
    withEditor,
    isPreviewMode = false,
}: CommentProps) {
    const t = useStrings(strings);
    const formType = useAppSelector(selectFormType);
    const comment = useAppSelector((state) => {
        return selectScorable(state)?.answers?.comments ?? "";
    });
    const showReadOnlyComments = comment.trim().length > 0;

    const dispatch = useAppDispatch();
    const agentHasRead = useAppSelector(selectAgentHasRead);

    const onPreviewSaveComment = isPreviewMode
        ? () => {
              if (agentHasRead) {
                  dispatch(scorableSliceActions.changeAgentReviewed());
              }
              handleGetPreviewScores(dispatch, formType);
          }
        : undefined;

    let commentDisplay = <></>;

    switch (withEditor) {
        case CommentEditor.OPEN:
            commentDisplay = (
                <OpenComment
                    label={t.labels.evaluatorComments}
                    comment={comment}
                    onUpdateComment={scorableSliceActions.fillEvaluatorComments}
                    characterLimit={DefaultLimits.generalCommentCharLimit}
                />
            );
            break;
        case CommentEditor.STANDALONE:
            commentDisplay = (
                <StandaloneComment
                    label={t.labels.evaluatorComments}
                    comment={comment}
                    canEditIndividually={true}
                    onUpdateComment={scorableSliceActions.fillEvaluatorComments}
                    onSaveComment={saveEvaluatorComment}
                    onPreviewSaveComment={onPreviewSaveComment}
                    characterLimit={DefaultLimits.generalCommentCharLimit}
                />
            );
            break;
        default:
            // read-only mode
            commentDisplay = showReadOnlyComments ? (
                <StandaloneComment
                    label={t.labels.evaluatorComments}
                    comment={comment}
                    canEditIndividually={false}
                    onUpdateComment={scorableSliceActions.fillEvaluatorComments}
                    onSaveComment={saveEvaluatorComment}
                    onPreviewSaveComment={onPreviewSaveComment}
                />
            ) : (
                <></>
            );
            break;
    }

    return commentDisplay;
}

export function PrivateComment({
    withEditor,
    isPreviewMode = false,
}: CommentProps) {
    const dispatch = useAppDispatch();
    const t = useStrings(strings);
    const formType = useAppSelector(selectFormType);
    const showPrivateComments = useAppSelector(selectHasAssignPermission);
    const comment = useAppSelector((state) => {
        return selectPrivateComments(state) ?? "";
    });

    const onPreviewSaveComment = isPreviewMode
        ? () => handleGetPreviewScores(dispatch, formType)
        : undefined;
    const showReadOnlyComments = comment.trim().length > 0;
    let commentDisplay = <></>;

    if (showPrivateComments) {
        switch (withEditor) {
            case CommentEditor.OPEN:
                commentDisplay = (
                    <OpenComment
                        label={t.labels.privateComments}
                        tooltip={t.labels.privateCommentsTooltip}
                        comment={comment}
                        onUpdateComment={
                            scorableSliceActions.fillPrivateComments
                        }
                        characterLimit={DefaultLimits.generalCommentCharLimit}
                    />
                );
                break;
            case CommentEditor.STANDALONE:
                commentDisplay = (
                    <StandaloneComment
                        label={t.labels.privateComments}
                        tooltip={t.labels.privateCommentsTooltip}
                        comment={comment}
                        canEditIndividually={true}
                        onUpdateComment={
                            scorableSliceActions.fillPrivateComments
                        }
                        onSaveComment={savePrivateComment}
                        onPreviewSaveComment={onPreviewSaveComment}
                        characterLimit={DefaultLimits.generalCommentCharLimit}
                    />
                );
                break;
            default:
                // read-only mode
                commentDisplay = showReadOnlyComments ? (
                    <StandaloneComment
                        label={t.labels.privateComments}
                        tooltip={t.labels.privateCommentsTooltip}
                        comment={comment}
                        canEditIndividually={false}
                        onUpdateComment={
                            scorableSliceActions.fillPrivateComments
                        }
                        onSaveComment={savePrivateComment}
                        onPreviewSaveComment={onPreviewSaveComment}
                    />
                ) : (
                    <></>
                );
                break;
        }
    }

    return commentDisplay;
}

function CommentLabel({ label, tooltip }: { label: string; tooltip?: string }) {
    return (
        <div className="CommentLabel">
            {label}
            {tooltip && (
                <div className="CommentTooltip">
                    <GuxIcon
                        className="CommentTooltipIcon"
                        iconName="alert-info"
                        screenreaderText={tooltip}
                    ></GuxIcon>
                    <GuxTooltip>{tooltip}</GuxTooltip>
                </div>
            )}
        </div>
    );
}

function OpenComment({
    label,
    tooltip,
    comment,
    onUpdateComment,
    characterLimit,
}: {
    label: string;
    tooltip?: string;
    comment: string;
    onUpdateComment: (payload: string) => {
        payload: string;
        type: string;
    };
    characterLimit?: number;
}) {
    const t = useStrings(strings);
    const dispatch = useAppDispatch();

    const hasError = characterLimit && comment.length > characterLimit;

    return (
        <div
            className="Comment OpenComment"
            data-testid="comments-box"
            data-invalid={hasError === true}
        >
            <GuxFormFieldTextarea
                resize="manual"
                labelPosition="above"
                screenreader-text={label}
            >
                <textarea
                    slot="input"
                    value={comment ?? ""}
                    onChange={({ currentTarget }) => {
                        dispatch(onUpdateComment(currentTarget.value));
                    }}
                ></textarea>
                <label slot="label">
                    <CommentLabel label={label} tooltip={tooltip} />
                </label>
                {hasError && (
                    <span slot="error">
                        {t.labels.commentCharLimitExceeded({
                            characterLimit: characterLimit,
                        })}
                    </span>
                )}
            </GuxFormFieldTextarea>
            <RemainingCharacters
                currentCount={comment.length}
                totalCount={characterLimit}
            />
        </div>
    );
}

function StandaloneComment({
    label,
    tooltip,
    comment,
    canEditIndividually,
    onUpdateComment,
    onSaveComment,
    onPreviewSaveComment,
    characterLimit,
}: {
    label: string;
    tooltip?: string;
    comment: string;
    canEditIndividually: boolean;
    onUpdateComment: (payload: string) => {
        payload: string;
        type: string;
    };
    onSaveComment: () => AsyncThunkAction<
        EvaluationResponse | undefined,
        {
            commentType: CommentType;
        },
        {
            state: RootState;
        }
    >;
    onPreviewSaveComment?: () => void;
    characterLimit?: number;
}) {
    const t = useStrings(strings);
    const dispatch = useAppDispatch();
    const hasError = characterLimit && comment.length > characterLimit;
    const [isEditing, setIsEditing] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [originalComment, setOriginalComment] = useState(comment);
    const isOperationInProgress = useAppSelector(selectIsOperationInProgress);

    return (
        <div
            className="Comment"
            data-testid="comments-box"
            data-invalid={hasError === true}
        >
            {isEditing ? (
                <>
                    <GuxFormFieldTextarea
                        resize="manual"
                        labelPosition="above"
                        screenreader-text={label}
                    >
                        <textarea
                            slot="input"
                            value={comment ?? ""}
                            disabled={isLoading}
                            onChange={({ currentTarget }) => {
                                dispatch(onUpdateComment(currentTarget.value));
                            }}
                        ></textarea>
                        <label slot="label">
                            <CommentLabel label={label} tooltip={tooltip} />
                        </label>
                        {hasError && (
                            <span slot="error">
                                {t.labels.commentCharLimitExceeded({
                                    characterLimit: characterLimit,
                                })}
                            </span>
                        )}
                    </GuxFormFieldTextarea>
                    <RemainingCharacters
                        currentCount={comment.length}
                        totalCount={characterLimit}
                    />
                    <div className="CommentEditButtons">
                        <GuxButton
                            accent="tertiary"
                            disabled={toGuxDisabled(
                                isLoading || isOperationInProgress
                            )}
                            aria-disabled={toGuxDisabled(
                                isLoading || isOperationInProgress
                            )}
                            onClick={() => {
                                setIsLoading(true);
                                if (onPreviewSaveComment) {
                                    onPreviewSaveComment();
                                    if (!hasError) {
                                        setOriginalComment(comment);
                                        setIsEditing(false);
                                    }
                                } else {
                                    dispatch(onSaveComment()).then((e) => {
                                        if ("error" in e) {
                                            toastService.notifyMajorAlert(
                                                "qualityFormViewer.footer.saveComment.fail.message"
                                            );
                                        } else {
                                            toastService.notifyMinorAlert(
                                                "qualityFormViewer.footer.saveComment.success.message"
                                            );
                                            setOriginalComment(comment);
                                            setIsEditing(false);
                                        }
                                    });
                                }
                                setIsLoading(false);
                            }}
                        >
                            {t.labels.saveComment}
                        </GuxButton>
                        <GuxButton
                            accent="secondary"
                            disabled={toGuxDisabled(isLoading)}
                            onClick={() => {
                                setIsLoading(true);
                                dispatch(onUpdateComment(originalComment));
                                setIsLoading(false);
                                setIsEditing(false);
                            }}
                        >
                            {t.labels.cancel}
                        </GuxButton>
                    </div>
                </>
            ) : (
                <>
                    <div
                        className="CommentLabelSection"
                        data-testid="label-comment-section"
                    >
                        <CommentLabel label={label} tooltip={tooltip} />
                        {canEditIndividually && (
                            <GuxButton
                                accent="ghost"
                                onClick={() => {
                                    setOriginalComment(comment);
                                    setIsEditing(true);
                                }}
                            >
                                <GuxIcon
                                    data-testid="edit-standalone-comment-button"
                                    iconName="edit"
                                    screenreaderText={t.labels.edit}
                                ></GuxIcon>
                            </GuxButton>
                        )}
                    </div>
                    {comment && (
                        <span
                            className="CommentReadOnly"
                            data-testid="comment-read-only"
                        >
                            {comment}
                        </span>
                    )}
                </>
            )}
        </div>
    );
}
